const MIN_BOOT_MS = 180;
const FONTS_WAIT_MS = 250;
const HERO_WAIT_MS = 800;
const BOOT_FAILSAFE_MS = 4500;
const BOOT_HARD_MAX_MS = 8000;
const FADE_OUT_MS = 300;
const MIN_ROUTE_MS = 420;
const MAX_ROUTE_MS = 2500;
const RESUME_STUCK_MS = 90 * 1000;
const RESUME_RELOAD_GUARD_KEY = 'vb_boot_resume_reload';

let bootStartedAt = typeof performance !== 'undefined' ? performance.now() : Date.now();
let routeHideTimer = 0;
let routeMaxTimer = 0;
let routeSeq = 0;
let hardMaxTimer = 0;
let resumeGuardStarted = false;
let bootGoneTimer = 0;
let networkNoticesReady = false;

export const NETWORK_NOTICES_READY_EVENT = 'vb:network-notices-ready';

export { BOOT_FAILSAFE_MS, BOOT_HARD_MAX_MS, HERO_WAIT_MS, MIN_ROUTE_MS, MAX_ROUTE_MS };

export function markAppBootStart() {
  bootStartedAt = typeof performance !== 'undefined' ? performance.now() : Date.now();
}

export function isNetworkNoticesReady() {
  return networkNoticesReady;
}

/** Offline / reconnect toasts only after first successful boot dismiss. */
export function markNetworkNoticesReady() {
  if (networkNoticesReady) return;
  networkNoticesReady = true;
  try {
    if (typeof window !== 'undefined') {
      window.__vbNetworkNoticesReady = true;
      window.dispatchEvent(new CustomEvent(NETWORK_NOTICES_READY_EVENT));
    }
  } catch {
    /* ignore */
  }
}

export function ensureNetworkNoticesReady() {
  if (networkNoticesReady) return true;
  try {
    const boot = typeof document !== 'undefined' ? document.getElementById('app-boot') : null;
    if (!boot || boot.classList.contains('app-boot--gone') || boot.dataset.dismissed === 'true') {
      markNetworkNoticesReady();
    }
  } catch {
    markNetworkNoticesReady();
  }
  return networkNoticesReady;
}

function getBootEl() {
  return document.getElementById('app-boot');
}

export function isAppBootOverlayActive() {
  const boot = getBootEl();
  if (!boot) return false;
  if (boot.classList.contains('app-boot--gone')) return false;
  if (boot.dataset.routeActive === 'true') return true;
  if (boot.dataset.dismissed === 'true') return false;
  return !boot.classList.contains('app-boot--hide');
}

function scheduleBootGone(boot) {
  if (!boot) return;
  window.clearTimeout(bootGoneTimer);
  const markGone = () => {
    boot.classList.add('app-boot--gone');
    boot.setAttribute('aria-hidden', 'true');
  };
  const onEnd = (event) => {
    if (event.target !== boot) return;
    if (event.propertyName && event.propertyName !== 'opacity') return;
    boot.removeEventListener('transitionend', onEnd);
    markGone();
  };
  boot.addEventListener('transitionend', onEnd);
  bootGoneTimer = window.setTimeout(() => {
    boot.removeEventListener('transitionend', onEnd);
    markGone();
  }, FADE_OUT_MS + 80);
}

function applyBootDismiss(boot) {
  if (!boot) return;
  boot.dataset.dismissed = 'true';
  boot.dataset.routeActive = 'false';
  boot.setAttribute('aria-busy', 'false');
  boot.classList.add('app-boot--hide');
  scheduleBootGone(boot);
  markNetworkNoticesReady();
}

export function forceDismissAppBoot() {
  const boot = getBootEl();
  if (!boot) return;
  window.clearTimeout(routeHideTimer);
  window.clearTimeout(routeMaxTimer);
  applyBootDismiss(boot);
}

export function dismissAppBoot() {
  const boot = getBootEl();
  if (!boot || boot.dataset.dismissed === 'true') return;

  const elapsed = (typeof performance !== 'undefined' ? performance.now() : Date.now()) - bootStartedAt;
  const wait = Math.max(0, MIN_BOOT_MS - elapsed);

  window.setTimeout(() => {
    if (!boot || boot.dataset.dismissed === 'true') return;
    applyBootDismiss(boot);
  }, wait);
}

export function showRoutePreloader() {
  const boot = getBootEl();
  if (!boot) return false;

  window.clearTimeout(routeHideTimer);
  window.clearTimeout(routeMaxTimer);
  window.clearTimeout(bootGoneTimer);

  boot.dataset.routeActive = 'true';
  boot.setAttribute('aria-busy', 'true');
  boot.setAttribute('aria-hidden', 'false');
  boot.classList.add('is-boot-ready');
  boot.classList.remove('app-boot--gone');
  boot.style.transition = 'none';
  boot.classList.remove('app-boot--hide');
  void boot.offsetWidth;
  boot.style.transition = '';
  return true;
}

export function hideRoutePreloader() {
  window.clearTimeout(routeHideTimer);
  window.clearTimeout(routeMaxTimer);
  const boot = getBootEl();
  if (!boot) return;
  boot.dataset.routeActive = 'false';
  boot.setAttribute('aria-busy', 'false');
  boot.classList.add('app-boot--hide');
  scheduleBootGone(boot);
}

export function beginRoutePreload() {
  const seq = ++routeSeq;
  const shown = showRoutePreloader();
  if (!shown) return { seq, cancel: () => {}, finish: () => {} };

  const startedAt = typeof performance !== 'undefined' ? performance.now() : Date.now();
  routeMaxTimer = window.setTimeout(() => {
    if (seq !== routeSeq) return;
    hideRoutePreloader();
  }, MAX_ROUTE_MS);

  return {
    seq,
    cancel() {
      if (seq !== routeSeq) return;
      window.clearTimeout(routeHideTimer);
      window.clearTimeout(routeMaxTimer);
      hideRoutePreloader();
    },
    finish() {
      if (seq !== routeSeq) return;
      const elapsed = (typeof performance !== 'undefined' ? performance.now() : Date.now()) - startedAt;
      const wait = Math.max(0, MIN_ROUTE_MS - elapsed);
      window.clearTimeout(routeHideTimer);
      routeHideTimer = window.setTimeout(() => {
        if (seq !== routeSeq) return;
        window.clearTimeout(routeMaxTimer);
        hideRoutePreloader();
      }, wait);
    },
  };
}

export function preloadImage(src) {
  return new Promise((resolve) => {
    if (!src) {
      resolve(false);
      return;
    }
    const img = new Image();
    let settled = false;
    const finish = (ok) => {
      if (settled) return;
      settled = true;
      resolve(ok);
    };
    img.onload = () => {
      if (typeof img.decode === 'function') {
        img.decode().then(() => finish(true), () => finish(true));
      } else {
        finish(true);
      }
    };
    img.onerror = () => finish(false);
    img.decoding = 'async';
    img.src = src;
    if (img.complete && img.naturalWidth > 0) {
      if (typeof img.decode === 'function') {
        img.decode().then(() => finish(true), () => finish(true));
      } else {
        finish(true);
      }
    }
  });
}

function waitMs(ms) {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

function nextPaint() {
  return new Promise((resolve) => {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => resolve());
    });
  });
}

export async function dismissAppBootWhenReady({
  heroSrc = null,
  heroTimeoutMs = HERO_WAIT_MS,
} = {}) {
  const fontsReady = document.fonts?.ready ?? Promise.resolve();
  const fontsTimeout = waitMs(FONTS_WAIT_MS);

  await Promise.race([
    fontsReady.catch(() => {}),
    fontsTimeout,
  ]);

  if (heroSrc) {
    await Promise.race([
      preloadImage(heroSrc),
      waitMs(heroTimeoutMs),
    ]);
  }

  await nextPaint();
  dismissAppBoot();
}

function tryResumeSoftReload() {
  try {
    if (sessionStorage.getItem(RESUME_RELOAD_GUARD_KEY) === '1') return false;
    sessionStorage.setItem(RESUME_RELOAD_GUARD_KEY, '1');
  } catch {
    return false;
  }
  try {
    const url = new URL(window.location.href);
    if (url.searchParams.has('_vb')) {
      url.searchParams.delete('_vb');
      window.location.replace(url.pathname + url.search + url.hash);
    } else {
      window.location.reload();
    }
  } catch {
    window.location.reload();
  }
  return true;
}

export function recoverStuckAppBoot({ allowReload = true } = {}) {
  if (!isAppBootOverlayActive()) return false;
  forceDismissAppBoot();
  if (!allowReload) return true;
  return tryResumeSoftReload();
}

export function startBootResumeGuard() {
  if (typeof window === 'undefined' || typeof document === 'undefined') return () => {};
  if (resumeGuardStarted) return () => {};
  resumeGuardStarted = true;

  let hiddenAt = document.hidden ? Date.now() : 0;

  window.clearTimeout(hardMaxTimer);
  hardMaxTimer = window.setTimeout(() => {
    forceDismissAppBoot();
  }, BOOT_HARD_MAX_MS);

  const onVisibility = () => {
    if (document.hidden) {
      hiddenAt = Date.now();
      return;
    }
    const hiddenFor = hiddenAt ? Date.now() - hiddenAt : 0;
    hiddenAt = 0;
    if (hiddenFor < RESUME_STUCK_MS) {
      if (isAppBootOverlayActive()) forceDismissAppBoot();
      return;
    }
    if (isAppBootOverlayActive()) {
      recoverStuckAppBoot({ allowReload: true });
    }
  };

  const onPageShow = (event) => {
    if (!event?.persisted) return;
    if (isAppBootOverlayActive()) {
      recoverStuckAppBoot({ allowReload: true });
    }
  };

  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('pageshow', onPageShow);

  try {
    if (!isAppBootOverlayActive()) {
      sessionStorage.removeItem(RESUME_RELOAD_GUARD_KEY);
    }
  } catch {}

  return () => {
    window.clearTimeout(hardMaxTimer);
    window.clearTimeout(bootGoneTimer);
    document.removeEventListener('visibilitychange', onVisibility);
    window.removeEventListener('pageshow', onPageShow);
    resumeGuardStarted = false;
  };
}
